import { LEGACY_KEYS } from "@hachi/core";
import { readJson, writeJson } from "./storage";
import type { KeyValue } from "./storage";

const b64url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/** Random URL-safe id of n characters (`randomId`). */
export function randomId(n = 24): string {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return b64url(a).slice(0, n);
}

export { b64url };

const DEVICE_ID = /^[A-Za-z0-9_-]{8,64}$/;

/** This browser's device id, created on first use. Same key and format as the current app. */
export function deviceId(store: KeyValue): string {
  let d = readJson<string>(store, LEGACY_KEYS.device);
  if (!d || !DEVICE_ID.test(d)) {
    d = `d_${randomId(20)}`;
    writeJson(store, LEGACY_KEYS.device, d);
  }
  return d;
}

/** A fresh device id, for when this browser's id belongs to another account. */
export function newDeviceId(store: KeyValue): string {
  writeJson(store, LEGACY_KEYS.device, null);
  return deviceId(store);
}

export interface DeviceHints {
  userAgent: string;
  maxTouchPoints: number;
  /** Running as an installed (Home Screen) app. */
  standalone: boolean;
}

/** "iPhone · Home Screen app", "Mac · Safari"…, shown in the device list (`deviceLabel`). */
export function deviceLabel({ userAgent: ua, maxTouchPoints, standalone }: DeviceHints): string {
  const ipad = /iPad/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
  const kind = ipad ? "iPad"
    : /iPhone/.test(ua) ? "iPhone"
    : /Android/.test(ua) ? (/Mobile/.test(ua) ? "Android phone" : "Android tablet")
    : /Macintosh/.test(ua) ? "Mac"
    : /Windows/.test(ua) ? "Windows PC"
    : /CrOS/.test(ua) ? "Chromebook"
    : /Linux/.test(ua) ? "Linux PC"
    : "Device";
  const browser = /EdgA?\//.test(ua) ? "Edge"
    : /SamsungBrowser/.test(ua) ? "Samsung Internet"
    : /Firefox|FxiOS/.test(ua) ? "Firefox"
    : /CriOS|Chrome\//.test(ua) ? "Chrome"
    : /Safari/.test(ua) ? "Safari"
    : "Browser";
  return `${kind} · ${standalone ? "Home Screen app" : browser}`;
}

/** "just now", "5 min ago", "today, 3:40 PM", "yesterday" or a date (`ago`). */
export function lastSeen(t: string | number | undefined, now = Date.now(), locale?: string): string {
  if (!t) return "";
  const d = new Date(t), s = (now - d.getTime()) / 1000;
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400 && d.getDate() === new Date(now).getDate()) return `today, ${new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(d)}`;
  if (s < 2 * 86400) return "yesterday";
  return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric" }).format(d);
}
